'use client';

import { useState } from 'react';
import {
    Sparkles,
    Copy,
    RefreshCw,
} from 'lucide-react';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { facebookTitleGeneratorContent } from '../../data/facebook-title-generator-content';

type PostType =
    | 'status'
    | 'link'
    | 'photo'
    | 'video'
    | 'event'
    | 'offer'
    | 'poll';

export default function FacebookPostGenerator() {
    const [topic, setTopic] = useState('');
    const [tone, setTone] = useState('friendly');
    const [postType, setPostType] = useState<PostType>('status');
    const [includeEmojis, setIncludeEmojis] = useState(true);
    const [includeHashtags, setIncludeHashtags] = useState(true);
    const [callToAction, setCallToAction] = useState('');
    const [posts, setPosts] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const tones = [
        { value: 'friendly', label: 'Friendly' },
        { value: 'professional', label: 'Professional' },
        { value: 'funny', label: 'Funny' },
        { value: 'inspirational', label: 'Inspirational' },
        { value: 'urgent', label: 'Urgent' },
    ];

    const postTypes = [
        { value: 'status', label: 'Status Update', icon: '📝' },
        { value: 'link', label: 'Link Share', icon: '🔗' },
        { value: 'photo', label: 'Photo Post', icon: '🖼️' },
        { value: 'video', label: 'Video Post', icon: '🎥' },
        { value: 'event', label: 'Event', icon: '📅' },
        { value: 'offer', label: 'Special Offer', icon: '🎁' },
        { value: 'poll', label: 'Poll', icon: '📊' },
    ];

    const callToActions = [
        { value: '', label: 'None' },
        { value: 'like', label: 'Like & Share' },
        { value: 'comment', label: 'Comment Below' },
        { value: 'share', label: 'Share with Friends' },
        { value: 'learn-more', label: 'Learn More' },
        { value: 'signup', label: 'Sign Up Now' },
        { value: 'shop-now', label: 'Shop Now' },
    ];

    const generateHashtags = (topic: string, count = 5): string => {
        if (!includeHashtags) return '';

        const keywords = topic
            .toLowerCase()
            .split(/\s+/)
            .filter((word) => word.length > 3)
            .map((word) => word.replace(/[^a-z0-9]/g, ''))
            .filter(Boolean);

        const common = [
            'socialmedia',
            'marketing',
            'business',
            'branding',
            'entrepreneur',
        ];

        const all = [...new Set([...keywords, ...common])];

        return all
            .slice(0, count)
            .map((tag) => `#${tag}`)
            .join(' ');
    };

    const generatePosts = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!topic.trim()) {
            setError('Please enter a topic');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const response = await fetch('/api/generate/facebook-posts', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    topic,
                    tone,
                    postType,
                    includeEmojis,
                    includeHashtags,
                    callToAction: callToAction || undefined,
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to generate posts');
            }

            const data = await response.json();
            setPosts(data.posts);
        } catch (err) {
            setError('Failed to generate posts. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const regeneratePost = async (index: number) => {
        if (!topic.trim()) return;

        setIsLoading(true);

        try {
            const response = await fetch('/api/generate/facebook-posts', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    topic,
                    tone,
                    postType,
                    includeEmojis,
                    includeHashtags,
                    callToAction: callToAction || undefined,
                    generateSingle: true,
                }),
            });

            const data = await response.json();

            const updated = [...posts];
            updated[index] = data.posts[0];
            setPosts(updated);
        } catch {
            setError('Failed to regenerate post.');
        } finally {
            setIsLoading(false);
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
    };

    return (
        <div className="max-w-4xl mx-auto p-6 space-y-12">
            {/* Header */}
            <div className="text-center">
                <ToolPageIntro title="Facebook Title Generator">
                Generate catchy Facebook post titles with our free online tool. Create engaging headlines that increase clicks and shares.
            </ToolPageIntro>
            </div>

            {/* Form */}
            <div className="bg-white rounded-xl shadow-sm p-6">
                <form onSubmit={generatePosts} className="space-y-6">
                    <input
                        type="text"
                        value={topic}
                        onChange={(e) => setTopic(e.target.value)}
                        placeholder="What’s your post about?"
                        className="w-full px-4 py-2 border rounded-lg"
                        required
                    />

                    <div className="grid md:grid-cols-2 gap-6">
                        <select
                            value={tone}
                            onChange={(e) => setTone(e.target.value)}
                            className="px-4 py-2 border rounded-lg"
                        >
                            {tones.map((t) => (
                                <option key={t.value} value={t.value}>
                                    {t.label}
                                </option>
                            ))}
                        </select>

                        <select
                            value={callToAction}
                            onChange={(e) => setCallToAction(e.target.value)}
                            className="px-4 py-2 border rounded-lg"
                        >
                            {callToActions.map((cta) => (
                                <option key={cta.value} value={cta.value}>
                                    {cta.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {error && (
                        <div className="text-red-600 text-sm">{error}</div>
                    )}

                    <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full bg-blue-600 text-white py-3 rounded-lg flex items-center justify-center gap-2"
                    >
                        {isLoading ? (
                            <>
                                <RefreshCw className="animate-spin h-5 w-5" />
                                Generating...
                            </>
                        ) : (
                            <>
                                <Sparkles className="h-5 w-5" />
                                Generate Posts
                            </>
                        )}
                    </button>
                </form>
            </div>

            {/* Results */}
            {posts.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
                    <h2 className="text-xl font-semibold">Generated Posts</h2>

                    {posts.map((post, index) => (
                        <div
                            key={index}
                            className="relative p-4 border rounded-lg"
                        >
                            <p className="whitespace-pre-line">{post}</p>

                            {includeHashtags && (
                                <p className="mt-3 text-blue-600 text-sm">
                                    {generateHashtags(topic)}
                                </p>
                            )}

                            <div className="absolute top-2 right-2 flex gap-2">
                                <button
                                    onClick={() =>
                                        copyToClipboard(
                                            post +
                                            (includeHashtags
                                                ? '\n\n' + generateHashtags(topic)
                                                : '')
                                        )
                                    }
                                >
                                    <Copy className="h-4 w-4 text-gray-500" />
                                </button>

                                <button onClick={() => regeneratePost(index)}>
                                    <RefreshCw className="h-4 w-4 text-gray-500" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Informational Content */}
            <InformationalContent
                title={facebookTitleGeneratorContent.title}
                description={facebookTitleGeneratorContent.description}
                content={facebookTitleGeneratorContent.content}
                faqs={facebookTitleGeneratorContent.faqs}
                relatedTools={facebookTitleGeneratorContent.relatedTools}
            />
        </div>
    );
}